import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useReducedMotion } from 'framer-motion';
import { MotionCta, RevealOnScroll } from './homeAtmosphere';

type Props = {
  onCta: () => void;
  /** Secondary CTA — typically scroll to product / how it works. */
  onSeeHow: () => void;
};

/** Chart scale top (×). Bars + break-even marks are % of this. */
const CHART_MAX = 6;

type MarketRow = {
  market: string;
  code: string;
  blended: number;
  breakEven: number;
  decision: 'scale' | 'hold' | 'fix';
  decisionLabel: string;
  color: string;
};

const MARKETS: MarketRow[] = [
  {
    market: 'Germany',
    code: 'DE',
    blended: 5.1,
    breakEven: 2.8,
    decision: 'scale',
    decisionLabel: 'Test scale',
    color: 'rgb(30, 237, 159)',
  },
  {
    market: 'Italy',
    code: 'IT',
    blended: 2.8,
    breakEven: 3.2,
    decision: 'hold',
    decisionLabel: 'Hold',
    color: '#f2bf68',
  },
  {
    market: 'France',
    code: 'FR',
    blended: 1.6,
    breakEven: 3.6,
    decision: 'fix',
    decisionLabel: 'Fix first',
    color: '#e98880',
  },
];

const pct = (value: number) => `${Math.min(100, (value / CHART_MAX) * 100)}%`;

/**
 * MCP as Kipify data layer for ChatGPT/Claude — decision dashboard, not AI essay.
 */
export const HomeMcpSection: React.FC<Props> = ({ onCta, onSeeHow }) => {
  const reduce = useReducedMotion();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [barsOn, setBarsOn] = useState(false);

  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    if (reduce) {
      setBarsOn(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setBarsOn(true);
          io.disconnect();
        }
      },
      { threshold: 0.3 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [reduce]);

  return (
    <RevealOnScroll
      id="mcp"
      sx={{
        padding: { xs: '56px 6%', md: '72px 10%' },
        width: '100%',
        boxSizing: 'border-box',
        borderTop: '1px solid rgba(255, 255, 255, 0.05)',
        background:
          'radial-gradient(640px 280px at 85% 20%, rgba(30, 237, 159, 0.1), transparent 60%), #06090e',
      }}
    >
      <Box
        sx={{
          maxWidth: 1100,
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'minmax(0, 1fr) minmax(0, 1.05fr)',
          },
          gap: { xs: '32px', md: '40px' },
          alignItems: 'start',
        }}
      >
        {/* Left — value prop (top-aligned with dashboard) */}
        <Box
          sx={{
            pt: { md: '4px' },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: '11px',
              marginBottom: '20px',
            }}
          >
            <Typography
              sx={{
                color: 'rgb(30, 237, 159)',
                fontSize: '0.75rem',
                fontWeight: 800,
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
              }}
            >
              Kipify MCP
            </Typography>
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '7px',
                padding: '5px 11px',
                borderRadius: '999px',
                border: '1px solid #33404a',
                color: '#9da9b5',
                fontSize: '0.7rem',
                fontWeight: 600,
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: 'rgb(30, 237, 159)',
                }}
                aria-hidden
              />
              ChatGPT & Claude
            </Box>
          </Box>

          <Typography
            variant="h2"
            sx={{
              color: 'white',
              fontSize: { xs: '1.85rem', md: '2.35rem' },
              fontWeight: 700,
              letterSpacing: '-0.03em',
              lineHeight: 1.12,
              marginBottom: '14px',
            }}
          >
            Ask what to scale.
            <Box
              component="span"
              sx={{ color: 'rgb(30, 237, 159)', display: 'block' }}
            >
              Know what actually pays off.
            </Box>
          </Typography>

          <Typography
            sx={{
              color: 'rgba(210, 218, 228, 0.75)',
              fontSize: '1.02rem',
              lineHeight: 1.65,
              maxWidth: '38ch',
              marginBottom: '26px',
            }}
          >
            Your ad platforms show you ROAS. Kipify connects the bigger picture:
            store revenue, ad spend, product mix and margins. Ask ChatGPT or
            Claude where to invest next—and understand why.
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: '18px',
            }}
          >
            <MotionCta onClick={onCta}>Start free trial</MotionCta>
            <Typography
              component="button"
              onClick={onSeeHow}
              sx={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                color: 'rgba(210, 217, 221, 0.9)',
                fontSize: '0.95rem',
                fontFamily: 'inherit',
                textDecoration: 'underline',
                textUnderlineOffset: '5px',
                textDecorationColor: '#65727c',
                '&:hover': { color: 'rgb(30, 237, 159)' },
              }}
            >
              See how it works
            </Typography>
          </Box>

          <Typography
            sx={{
              marginTop: '22px',
              fontSize: '0.75rem',
              color: '#70808d',
            }}
          >
            <Box component="span" sx={{ color: '#9daab3', fontWeight: 650 }}>
              Connected context:
            </Box>{' '}
            Shopify · Meta Ads · Google Ads · GA4 · Search Console
          </Typography>
        </Box>

        {/* Right — decision dashboard */}
        <Box
          ref={panelRef}
          sx={{
            borderRadius: '18px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            background:
              'linear-gradient(160deg, rgba(14, 22, 34, 0.98) 0%, rgba(8, 12, 18, 1) 100%)',
            boxShadow: '0 24px 64px rgba(0, 0, 0, 0.4)',
            overflow: 'hidden',
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '12px',
              px: { xs: '14px', md: '18px' },
              py: '12px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
              <Box
                sx={{
                  width: 9,
                  height: 9,
                  borderRadius: '50%',
                  backgroundColor: 'rgb(30, 237, 159)',
                  boxShadow: '0 0 12px rgba(43, 230, 170, 0.25)',
                }}
              />
              <Typography
                sx={{
                  color: '#9cabb6',
                  fontSize: { xs: '0.75rem', md: '0.82rem' },
                }}
              >
                <Box
                  component="strong"
                  sx={{ color: '#e4eef2', fontWeight: 670 }}
                >
                  Claude / ChatGPT
                </Box>{' '}
                <Box component="span" sx={{ color: '#627582' }}>
                  ×
                </Box>{' '}
                Kipify MCP
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: '0.68rem',
                letterSpacing: '0.075em',
                textTransform: 'uppercase',
                color: '#80919e',
                flexShrink: 0,
              }}
            >
              Illustrative example
            </Typography>
          </Box>

          <Box sx={{ p: { xs: '14px', md: '16px 18px' } }}>
            <Box
              sx={{
                maxWidth: '92%',
                ml: 'auto',
                mb: '14px',
                px: '14px',
                py: '10px',
                borderRadius: '14px 14px 4px 14px',
                background: 'rgba(30, 237, 159, 0.14)',
                border: '1px solid rgba(30, 237, 159, 0.28)',
              }}
            >
              <Typography
                sx={{
                  color: '#e4f7ef',
                  fontSize: { xs: '0.82rem', md: '0.95rem' },
                  fontWeight: 630,
                }}
              >
                Why is revenue falling when my ad ROAS is improving?
              </Typography>
            </Box>

            <Box
              sx={{
                p: { xs: '14px', md: '16px 18px' },
                borderRadius: '14px',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                background: 'rgba(7, 12, 18, 0.57)',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '12px',
                  flexWrap: 'wrap',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.7rem',
                    letterSpacing: '0.14em',
                    color: 'rgb(30, 237, 159)',
                    textTransform: 'uppercase',
                    fontWeight: 800,
                  }}
                >
                  Cross-platform analysis
                </Typography>
                <Typography sx={{ fontSize: '0.7rem', color: '#84949f' }}>
                  Last 30 days · sample data
                </Typography>
              </Box>

              <Typography
                sx={{
                  color: 'white',
                  fontSize: { xs: '1.1rem', md: '1.25rem' },
                  fontWeight: 700,
                  letterSpacing: '-0.025em',
                  mt: '7px',
                  mb: '6px',
                }}
              >
                Blended ROAS vs break-even
              </Typography>
              <Typography
                sx={{
                  color: '#82919e',
                  fontSize: '0.75rem',
                  lineHeight: 1.5,
                  m: 0,
                }}
              >
                Store-level revenue vs total ad spend, compared with estimated
                break-even by country.
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  gap: '18px',
                  flexWrap: 'wrap',
                  my: '16px',
                  fontSize: '0.7rem',
                  color: '#9aa8b2',
                }}
              >
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <Box
                    sx={{
                      width: 18,
                      height: 8,
                      borderRadius: '10px',
                      backgroundColor: 'rgb(30, 237, 159)',
                    }}
                  />
                  Blended ROAS
                </Box>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '7px',
                  }}
                >
                  <Box
                    sx={{
                      height: 12,
                      borderLeft: '2px dashed #e6ecf0',
                    }}
                  />
                  Break-even ROAS
                </Box>
              </Box>

              {MARKETS.map((row) => {
                const delta = row.blended - row.breakEven;
                const above = delta >= 0;
                return (
                  <Box
                    key={row.code}
                    sx={{
                      py: '10px',
                      borderTop: '1px solid rgba(71, 85, 96, 0.27)',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '8px',
                        mb: '10px',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: { xs: '0.75rem', md: '0.82rem' },
                          fontWeight: 680,
                          color: 'white',
                        }}
                      >
                        {row.market}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: { xs: '0.7rem', md: '0.75rem' },
                          color: '#8e9da8',
                        }}
                      >
                        Break-even {row.breakEven.toFixed(1)}×{' '}
                        <Box
                          component="strong"
                          sx={{
                            color: 'white',
                            fontSize: { xs: '0.82rem', md: '0.95rem' },
                            fontVariantNumeric: 'tabular-nums',
                            ml: '5px',
                          }}
                        >
                          {row.blended.toFixed(1)}×
                        </Box>
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        height: 12,
                        backgroundColor: '#1e2b34',
                        borderRadius: '99px',
                        position: 'relative',
                        overflow: 'visible',
                      }}
                      role="img"
                      aria-label={`${row.market}: blended ${row.blended}×, break-even ${row.breakEven}×`}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: '0 auto 0 0',
                          width: barsOn ? pct(row.blended) : 0,
                          backgroundColor: row.color,
                          borderRadius: '99px',
                          transition: reduce
                            ? 'none'
                            : 'width 0.85s cubic-bezier(0.22, 1, 0.36, 1)',
                        }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          left: pct(row.breakEven),
                          top: '-6px',
                          height: 24,
                          borderLeft: '2px dashed #eaf5f6',
                          opacity: 0.9,
                        }}
                        aria-hidden
                      />
                    </Box>

                    <Typography
                      sx={{
                        fontSize: '0.7rem',
                        mt: '8px',
                        textAlign: 'right',
                        color: row.color,
                        fontWeight: 680,
                      }}
                    >
                      {above ? '+' : '−'}
                      {Math.abs(delta).toFixed(1)}× {above ? 'above' : 'below'}{' '}
                      break-even
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            <Box
              sx={{
                mt: '14px',
                borderRadius: '14px',
                background: 'linear-gradient(115deg, #152923, #141b22 70%)',
                border: '1px solid rgba(43, 230, 170, 0.21)',
                p: { xs: '14px', md: '16px 18px' },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  flexWrap: 'wrap',
                  mb: '9px',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    color: 'rgb(30, 237, 159)',
                    letterSpacing: '0.13em',
                    textTransform: 'uppercase',
                    fontWeight: 700,
                  }}
                >
                  Kipify insight
                </Typography>
                <Box
                  component="span"
                  sx={{
                    fontSize: '0.7rem',
                    border: '1px solid #2a735c',
                    color: '#a4f8d1',
                    px: '9px',
                    py: '2px',
                    borderRadius: '999px',
                  }}
                >
                  Actionable next step
                </Box>
              </Box>

              <Typography
                sx={{
                  color: 'white',
                  fontSize: { xs: '1rem', md: '1.05rem' },
                  fontWeight: 740,
                  lineHeight: 1.3,
                  mb: '7px',
                }}
              >
                Platform ROAS rose. Store revenue didn&apos;t. Start with France
                and Italy.
              </Typography>
              <Typography
                sx={{
                  color: '#c5d0d3',
                  fontSize: '0.82rem',
                  lineHeight: 1.55,
                  m: 0,
                }}
              >
                Campaign ROAS can look healthier while blended results by
                country fall below break-even. Germany still has headroom; Italy
                and France need mix and spend fixes before more budget.
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  gap: '8px',
                  flexWrap: 'wrap',
                  mt: '13px',
                }}
              >
                {[
                  { code: 'DE', label: 'Test scale', tone: 'scale' as const },
                  { code: 'IT', label: 'Hold', tone: 'hold' as const },
                  { code: 'FR', label: 'Fix first', tone: 'fix' as const },
                ].map((chip) => (
                  <Box
                    key={chip.code}
                    component="span"
                    sx={{
                      fontSize: '0.7rem',
                      fontWeight: 690,
                      borderRadius: '8px',
                      px: '9px',
                      py: '6px',
                      border:
                        chip.tone === 'scale'
                          ? '1px solid #3b5153'
                          : chip.tone === 'hold'
                            ? '1px solid #61523a'
                            : '1px solid #63433f',
                      color:
                        chip.tone === 'scale'
                          ? '#d2dedb'
                          : chip.tone === 'hold'
                            ? '#f1cb8f'
                            : '#efa69f',
                    }}
                  >
                    {chip.code} — {chip.label}
                  </Box>
                ))}
              </Box>
            </Box>

            <Typography
              sx={{
                pt: '13px',
                color: '#93a5b1',
                fontSize: '0.7rem',
                lineHeight: 1.5,
              }}
            >
              <Box component="strong" sx={{ color: '#d3dce0' }}>
                Google Brand · 7.2× reported ROAS:
              </Box>{' '}
              protect valuable coverage, but evaluate incrementality before
              treating it as a growth opportunity.
            </Typography>

            <Typography
              sx={{
                pt: '13px',
                fontSize: '0.65rem',
                lineHeight: 1.5,
                color: '#687b89',
              }}
            >
              Illustrative numbers, not a live account. Break-even estimates
              require reliable product-level costs and consistent country
              definitions. Platform-attributed ROAS and blended ROAS are not
              equivalent metrics.
            </Typography>
          </Box>
        </Box>
      </Box>
    </RevealOnScroll>
  );
};
